import React, { useMemo, useState } from 'react';
import DiaryEmbed from './DiaryEmbed';

/**
 * The diary in the conversation: a summary line, the REAL diary, and the
 * overlaps spelled out.
 *
 * The drawing is not done here. DiaryEmbed mounts the product's own viewport,
 * which is why this file is short — the first version drew its own lanes and
 * was a second, worse diary. Charlie asked the obvious question: why are we
 * not rendering the v2 diary? There was no good answer.
 *
 * What this DOES add is the two things a picture cannot say on its own. The
 * headline leads with what they can act on, because "1% of the day booked" is
 * true and tells nobody anything while "1 booking, 94 hours free" is the same
 * fact shaped like a decision. And overlaps are listed in words, because two
 * blocks drawn on top of each other do not say WHO is doubled up.
 *
 * CLASHES ARE STATED, NOT SCOLDED. A salon double-books deliberately — colour
 * processing over a cut, two heads at one basin, a walk-in over a soft hold.
 * Nothing here calls it an error or offers to fix it. Preventing overlaps
 * belongs on the client side, where the salon decides what is bookable.
 */

const dayLabel = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

export default function DiaryCard({ card }) {
    const days = useMemo(() => (card && card.days) || [], [card]);
    const [index, setIndex] = useState(0);

    if (!days.length) return null;

    const day = days[Math.min(index, days.length - 1)];

    const clashesToday = (card.clashes || []).filter((c) => c.date === day.date);
    const clashedLanes = new Set(clashesToday.map((c) => c.staffId));

    // Summary only — the embed draws who is on. Counted over people actually
    // rota'd, so a salon with twenty dormant staff records does not report
    // hundreds of free hours that nobody was ever going to work.
    const working = (day.lanes || []).filter((l) => l.rotaStart != null && !l.off);

    const bookingCount = working.reduce(
        (n, l) => n + (l.blocks || []).filter((b) => b.kind === 'booking').length, 0);
    const freeMinutes = working.reduce(
        (n, l) => n + Math.max(0, (l.openMinutes || 0) - (l.bookedMinutes || 0)), 0);
    const freeHours = Math.round(freeMinutes / 60);


    return (
        <div className="agt-card agt-diary">
            <div className="agt-diary-head">
                <div>
                    <h4 className="agt-card-title">{dayLabel(day.date)}</h4>
                    <p className="agt-diary-sub">
                        {/* Leads with the thing they can act on. "1% of the day
                            booked" is true and tells nobody anything; "1
                            booking, 94 hours free" is the same fact in the
                            shape of a decision. */}
                        {bookingCount === 0
                            ? 'Nothing booked'
                            : `${bookingCount} booking${bookingCount > 1 ? 's' : ''}`}
                        {freeHours > 0 && ` · ${freeHours} hours free`}
                        {clashesToday.length > 0 && ` · ${clashesToday.length} overlap${clashesToday.length > 1 ? 's' : ''}`}
                    </p>
                </div>
                {days.length > 1 && (
                    <div className="agt-diary-nav">
                        <button type="button" aria-label="Previous day"
                            disabled={index === 0} onClick={() => setIndex((i) => i - 1)}>←</button>
                        <span>{index + 1}/{days.length}</span>
                        <button type="button" aria-label="Next day"
                            disabled={index >= days.length - 1} onClick={() => setIndex((i) => i + 1)}>→</button>
                    </div>
                )}
            </div>

            {/* The REAL diary. Staff as columns, time down the side,
                horizontal scroll through the team — the product's own
                component rather than a second one drawn here. */}
            <DiaryEmbed date={day.date} />

            {clashesToday.length > 0 && (
                <div className="agt-diary-clashes">
                    {/* Stated, not flagged as a problem. Overlapping is allowed
                        on this side of the product and often deliberate. */}
                    <p className="agt-diary-clash-title">Overlapping</p>
                    {clashesToday.slice(0, 4).map((c, i) => (
                        <p className="agt-diary-clash" key={i}>
                            <strong>{c.who}</strong> {c.a} over {c.b}
                        </p>
                    ))}
                    {clashesToday.length > 4 && (
                        <p className="agt-diary-clash">and {clashesToday.length - 4} more.</p>
                    )}
                </div>
            )}
        </div>
    );
}
